import {
  Volume,
  VolumeChange,
  type AttachVolumeRequest,
  type CreateVolumeRequest,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./client.js";

const key = (projectId: string) => ["projects", projectId, "volumes"] as const;

/** The project's volumes; live updates refresh them through the `projects` key. */
export const useVolumes = (projectId: string) =>
  useQuery({
    queryKey: key(projectId),
    queryFn: () => api(`/projects/${projectId}/volumes`, { schema: z.array(Volume) }),
  });

/** Volume writes also change what servers show, so every project query is refreshed. */
function useVolumeMutation<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: ["projects"] }),
  });
}

const base = (projectId: string) => `/projects/${projectId}/volumes`;

export const useCreateVolume = (projectId: string) =>
  useVolumeMutation((body: CreateVolumeRequest) =>
    api(base(projectId), {
      method: "POST",
      body,
      schema: VolumeChange,
      idempotencyKey: crypto.randomUUID(),
    }),
  );

export const useUpdateVolume = (projectId: string) =>
  useVolumeMutation(
    ({ id, ...body }: { id: string; name?: string; labels?: Record<string, string> }) =>
      api(`${base(projectId)}/${id}`, { method: "PATCH", body, schema: Volume }),
  );

const useVolumeAction = <B>(projectId: string, name: string) =>
  useVolumeMutation(({ id, body }: { id: string; body?: B }) =>
    api(`${base(projectId)}/${id}/actions/${name}`, {
      method: "POST",
      ...(body === undefined ? {} : { body }),
      schema: VolumeChange,
    }),
  );

export const useAttachVolume = (projectId: string) =>
  useVolumeAction<AttachVolumeRequest>(projectId, "attach");
export const useDetachVolume = (projectId: string) => useVolumeAction<never>(projectId, "detach");
export const useResizeVolume = (projectId: string) =>
  useVolumeAction<{ sizeGb: number }>(projectId, "resize");
export const useVolumeProtection = (projectId: string) =>
  useVolumeAction<{ delete: boolean }>(projectId, "protection");

export const useDeleteVolume = (projectId: string) =>
  useVolumeMutation((id: string) => api(`${base(projectId)}/${id}`, { method: "DELETE" }));
